/* eslint-disable import/no-cycle */
/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2023, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *
 *           _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 */
import { Inject, Injectable } from '@angular/core';
import { AeroDialogService } from 'src/aero/aero-dialog/aero-dialog.service';
import { ButtonStyle } from 'src/aero/aero-button/aero-button.component';
import { TranslationService } from 'src/common/translation.service';
import { AssignPageDialogComponent } from './assign-page-dialog/assign-page-dialog.component';
import { AssignmentHttpService } from './assignment-http.service';
import { BULB_BUSY_COVER_SERVICE } from '../angularjs.providers';

/**
 * Our assignmenet service which opens modal related to assignment.
 */
@Injectable({
  providedIn: 'root',
})
export class AssignmentService {

  /**
   *  Contains group id.
   */
  groupId: string;

  /**
   * Contains folder id.
   */
  folderId: string;

  /**
   * Contains folder Name.
   */
  folderName: string;

  /**
   * Contains current Unit id.
   */
  unitId: string;

  /**
   *  contains assignment group and folder object.
   */
  assignPageList: any;

  /**
   * Default Constructor.
   * @param http Our assignment http service
   * @param dialogService Default dialog service.
   * @param $translate Translation Service
   * @param state state of angularjs.
   * @param bulbBusyCoverService show busy indicator.
   */
  constructor(
    private http: AssignmentHttpService,
    private dialogService : AeroDialogService,
    private $translate : TranslationService,
    @Inject('$state') private state,
    @Inject(BULB_BUSY_COVER_SERVICE) private bulbBusyCoverService,
  ) { }

  /**
   * Show assign page Dialog if there are groups.
   * @param unitId contains current unit id.
   * @param isAssignCollection whether assigning a collection.
   */
  showAssignPageDialog(unitId: string, isAssignCollection: boolean) : void {
    this.unitId = unitId;
    this.http.getAssignmentFolders().subscribe((response) => {
      this.assignPageList = response.content;
      if (response.hasContent) {
        this.showAssignmentModal(unitId, isAssignCollection);
      } else {
        this.showNoGroupModal();
      }
    });
  }

  /**
   * Show non bulb plus users warning modal.
   * @param ineligibleAccounts Contains list of non bulb+ users.
   * @param isAssignCollection whether assigning a collection.
   */
  showNonBulbPlusModal(ineligibleAccounts, isAssignCollection: boolean) {
    this.dialogService.showDialog({
      svgIconName: 'exclamation-triangle',
      iconClass: 'aero-alert',
      styleClass: 'assign-page',
      detail: this.$translate.translateInstant('assignment_bulbplus_warning'),
      closable: true,
      primaryStyle: ButtonStyle.PRIMARY_2,
      secondaryStyle: ButtonStyle.GREY,
      primaryLabel: this.$translate.translateInstant('assignment_continue_button'),
      onPrimary: () => {
        const params = {
          groupId: this.groupId,
          folderId: this.folderId !== '' ? this.folderId : null,
          folderName: this.folderName,
          unitId: this.unitId,
        };
        this.checkNonBulbPlus(params, true, isAssignCollection);
      }, // eslint-disable-line
      closeOnPrimary: true,
      closeOnSecondary: true,
      customContentComponent: AssignPageDialogComponent,
      data: {
        modelName: 'non-bulb-plus',
        ineligibleAccounts,
      },
    });
  }

  /**
   * Check non bulb plus user.
   * @param params parameter for dialog.
   * @param force boolean value for server call.
   * @param isAssignCollection whether assigning a collection.
   */
  checkNonBulbPlus(params, force, isAssignCollection: boolean) {
    const stopBusyIndicatorCallback = this.bulbBusyCoverService.showBusy();
    this.http.assignmentGroups(params, force, isAssignCollection).subscribe((response) => {
      stopBusyIndicatorCallback();
      if (response.status === 'NOT_POSTED' && response.ineligibleAccounts) {
        this.showNonBulbPlusModal(response.ineligibleAccounts, isAssignCollection);
      }
    });
  }

  /**
   * show assignment modal.
   * @param unitId current unit id.
   * @param isAssignCollection whether assigning a collection.
   */
  showAssignmentModal(unitId, isAssignCollection: boolean) {
    this.dialogService.showDialog({
      title: this.$translate.translateInstant('assignment_page'),
      svgIconName: 'three-arrows',
      iconClass: 'three-arrows-color',
      styleClass: 'assign-page',
      closable: true,
      primaryStyle: ButtonStyle.PRIMARY_2,
      secondaryStyle: ButtonStyle.GREY,
      primaryLabel: this.$translate.translateInstant('assignment_triple_dot_label'),
      onPrimary: () => {
        const params = {
          groupId: this.groupId,
          folderId: this.folderId || null,
          folderName: this.folderName,
          unitId,
        };
        this.checkNonBulbPlus(params, false, isAssignCollection);
      }, // eslint-disable-line
      closeOnPrimary: true,
      closeOnSecondary: true,
      primaryDisabled: true,
      data: {
        unitId,
        modelName: 'assign-page',
        assignPageList: this.assignPageList,
      },
      customContentComponent: AssignPageDialogComponent,
    });
  }

  /**
   * show no group modal.
   */
  showNoGroupModal() {
    this.dialogService.showDialog({
      svgIconName: 'exclamation-triangle',
      iconClass: 'aero-alert',
      styleClass: 'assign-page',
      title: this.$translate.translateInstant('assignment_no_group'),
      detail: this.$translate.translateInstant('assignment_no_group_body'),
      closable: true,
      showSecondary: false,
      primaryStyle: ButtonStyle.PRIMARY_2,
      primaryLabel: this.$translate.translateInstant('assignment_no_group_button'),
      onPrimary: () => {
        this.state.go('createGroup');
      }, // eslint-disable-line
      closeOnPrimary: true,
    });
  }
}
